import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Select,
  Pagination,
  LoadingSkeleton,
} from '@nexora/design-system';
import {
  useServiceOrders,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
  SERVICE_ORDER_PRIORITY_LABEL,
  SERVICE_ORDER_PRIORITY_VARIANT,
  SERVICE_ORDER_TYPE_LABEL,
  formatHours,
  type ServiceOrder,
} from './useServiceOrders';
import { formatCurrency, formatDate } from '../../shared/utils/format';

interface ServiceOrdersPageProps {
  /** Navegacao injetada pelo router (fora de escopo aqui). Default: URL absoluta. */
  onOpenOrder?: (id: string) => void;
}

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'OPEN', label: 'Aberta' },
  { value: 'IN_PROGRESS', label: 'Em Andamento' },
  { value: 'WAITING_PART', label: 'Aguardando Peça' },
  { value: 'COMPLETED', label: 'Concluída' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

/**
 * Pagina de listagem das Ordens de Servico (OS) — `GET /manufacturing/service-orders`.
 *
 * Consome o envelope paginado `{ data, total, page, limit }` com filtros de
 * status e busca (numero, titulo, cliente, equipamento).
 */
export function ServiceOrdersPage({ onOpenOrder }: ServiceOrdersPageProps): JSX.Element {
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useServiceOrders({
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    search: searchTerm || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const orders = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* Somente numeros vindos do servidor: `total` e a soma das colunas da
     pagina carregada (rotulada como tal para nao enganar). Nao existe
     endpoint de contagens para OS como ha para OP. */
  const pageHours = useMemo(
    () => orders.reduce((acc, o) => acc + (o.totalHours ?? 0), 0),
    [orders],
  );
  const pageMaterial = useMemo(
    () => orders.reduce((acc, o) => acc + (o.totalMaterialCost ?? 0), 0),
    [orders],
  );

  const openOrder = (id: string): void => {
    if (onOpenOrder) {
      onOpenOrder(id);
      return;
    }
    window.location.assign(`/manufacturing/service-orders/${id}`);
  };

  return (
    <div className="so-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Ordens de Serviço</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Gestão das ordens de serviço técnicas: abertura, acompanhamento, apontamento de horas,
            materiais e histórico.
          </p>
        </div>
      </div>

      {/* ── Indicadores (fonte: servidor + soma da página carregada) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-md mb-lg">
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">assignment</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Total de OS</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{total}</div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">
            {statusFilter === 'ALL' ? 'Sem filtro de status' : `Filtro: ${STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label}`}
          </p>
        </div>
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">schedule</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Horas apontadas</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{formatHours(pageHours)}</div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Soma da página atual</p>
        </div>
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">inventory_2</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Custo em materiais</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{formatCurrency(pageMaterial)}</div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Soma da página atual</p>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="so-filters">
        <Select
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </Select>
        <Input
          label="Buscar"
          placeholder="Nº da OS, título, cliente ou equipamento..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {/* ── Error ── */}
      {error && !isLoading && (
        <Card padding="2rem">
          <div className="state-error" role="alert">
            <p>Erro ao carregar ordens de serviço</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {/* ── Empty ── */}
      {!isLoading && !error && orders.length === 0 && (
        <Card padding="2rem">
          <div className="state-empty">
            <p>
              {searchTerm || statusFilter !== 'ALL'
                ? 'Nenhuma ordem de serviço encontrada com esses filtros.'
                : 'Nenhuma ordem de serviço encontrada.'}
            </p>
          </div>
        </Card>
      )}

      {/* ── Lista ── */}
      {!isLoading && !error && orders.length > 0 && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {total} {total === 1 ? 'ordem' : 'ordens'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Fila de ordens de serviço</h3>
          </CardHeader>
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Nº OS</th>
                  <th>Título</th>
                  <th>Cliente</th>
                  <th>Equipamento</th>
                  <th>Status</th>
                  <th>Prioridade</th>
                  <th style={{ textAlign: 'right' }}>Horas</th>
                  <th style={{ textAlign: 'right' }}>Materiais</th>
                  <th>Prev. início</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o: ServiceOrder) => (
                  <tr key={o.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{o.number}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{o.title}</div>
                      {o.type && (
                        <div className="so-row-sub">
                          {SERVICE_ORDER_TYPE_LABEL[o.type]} · {formatDate(o.createdAt)}
                        </div>
                      )}
                    </td>
                    <td>{o.customerRef ?? '—'}</td>
                    <td>{o.equipmentRef ?? '—'}</td>
                    <td>
                      <Badge variant={SERVICE_ORDER_STATUS_VARIANT[o.status]} size="sm">
                        {SERVICE_ORDER_STATUS_LABEL[o.status]}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={SERVICE_ORDER_PRIORITY_VARIANT[o.priority]} size="sm" dot>
                        {SERVICE_ORDER_PRIORITY_LABEL[o.priority]}
                      </Badge>
                    </td>
                    <td className="so-num">{formatHours(o.totalHours)}</td>
                    <td className="so-num">{formatCurrency(o.totalMaterialCost)}</td>
                    <td>{o.plannedStart ? formatDate(o.plannedStart) : '—'}</td>
                    <td>
                      <Button size="sm" variant="ghost" onClick={() => openOrder(o.id)}>
                        Detalhes
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="so-pagination">
            <span className="so-page-info">
              Mostrando {orders.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}-
              {Math.min(total, (page - 1) * PAGE_SIZE + orders.length)} de {total}{' '}
              {total === 1 ? 'ordem' : 'ordens'}
            </span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </Card>
      )}

      <style>{`
        .so-page { padding: 1.5rem; }
        .so-kpi {
          background: #ffffff;
          border: 1px solid #c4c6cf;
          border-radius: 8px;
          padding: 1rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }
        .so-kpi__top {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          margin-bottom: 0.25rem;
        }
        .so-kpi__top .material-symbols-outlined { font-size: 18px; color: #43474e; }
        .so-filters { display: flex; gap: 0.75rem; margin-bottom: 1rem; flex-wrap: wrap; }
        .so-filters > * { min-width: 180px; flex: 1; max-width: 320px; }
        .so-table-wrap { overflow-x: auto; }
        .so-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .so-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600;
          font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .so-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; vertical-align: top; }
        .so-table tbody tr:hover td { background: #eceef0; }
        .so-row-sub { font-size: 0.75rem; color: #74777f; margin-top: 0.125rem; }
        .so-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .so-pagination {
          display: flex; align-items: center; justify-content: space-between;
          gap: 1rem; padding: 1rem; flex-wrap: wrap;
        }
        .so-page-info { font-size: 0.8125rem; color: #43474e; }
        .state-error { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 1rem; color: #74777f; }
      `}</style>
    </div>
  );
}